import {
    RadialBarChart,
    RadialBar,
    PolarAngleAxis,
    ResponsiveContainer,
} from "recharts";
import { useI18n } from "@/i18n/I18nProvider";

const semData = [{ name: "S.E.M.", value: 57.54, fill: "#D4FF00" }];

export default function Hero() {
    const { t } = useI18n();
    const h = t.hero;
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden noise"
        >
            <div className="absolute inset-0 court-bg opacity-50" aria-hidden />
            <div
                className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(closest-side, rgba(212,255,0,0.10), transparent 70%)",
                }}
                aria-hidden
            />
            <div className="absolute left-0 right-0 top-[55%] h-px bg-white/5" aria-hidden />
            <div
                className="absolute left-1/2 top-0 bottom-0 w-px bg-white/5 hidden md:block"
                aria-hidden
            />

            <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-7">
                        <div
                            data-testid="hero-eyebrow"
                            className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-xs tracking-[0.18em] uppercase font-bold text-[#D4FF00]"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-[#D4FF00] pulse-dot" />
                            {h.eyebrow}
                        </div>

                        <h1
                            data-testid="hero-heading"
                            className="font-display font-bold uppercase mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] heading-clamp tracking-tighter"
                        >
                            {h.headlinePart1}
                            <br />
                            {h.headlinePart2}{" "}
                            <span className="text-[#D4FF00] lime-text-glow">
                                {h.headlineAccent}
                            </span>
                        </h1>

                        <p className="mt-7 text-lg md:text-xl text-white/65 max-w-xl leading-relaxed">
                            {h.sub}
                        </p>

                        <div className="mt-9 flex flex-wrap items-center gap-3">
                            <a
                                href="#cta"
                                data-testid="hero-primary-cta"
                                className="inline-flex items-center gap-2 px-6 py-4 rounded-full bg-[#D4FF00] hover:bg-[#BDF000] text-[#04140D] font-bold tracking-tight transition-all hover:scale-[1.03] active:scale-[0.98] lime-glow"
                            >
                                {h.ctaPrimary}
                                <span aria-hidden>→</span>
                            </a>
                            <a
                                href="#product"
                                data-testid="hero-secondary-cta"
                                className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-white/15 hover:border-white/40 hover:bg-white/[0.03] text-white font-semibold transition-all"
                            >
                                {h.ctaSecondary}
                            </a>
                        </div>

                        <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
                            {h.stats.map(([n, label]) => (
                                <div key={label}>
                                    <div className="font-display text-3xl md:text-4xl font-bold tabular text-white">
                                        {n}
                                    </div>
                                    <div className="text-xs mt-1.5 text-white/50 uppercase tracking-wider">
                                        {label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div
                            data-testid="hero-visual-card"
                            className="relative rounded-2xl border border-white/10 bg-[#071B12]/80 backdrop-blur-md p-6 md:p-7 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#D4FF00]">
                                    {h.semLabel}
                                </div>
                                <div className="flex items-center gap-1.5 text-[10px] text-white/55 uppercase tracking-widest">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#D4FF00] pulse-dot" />
                                    {h.live}
                                </div>
                            </div>
                            <div className="text-xs text-white/50 mb-3">{h.semSub}</div>

                            <div className="relative h-[240px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <RadialBarChart
                                        cx="50%"
                                        cy="60%"
                                        innerRadius="78%"
                                        outerRadius="100%"
                                        barSize={18}
                                        startAngle={180}
                                        endAngle={0}
                                        data={semData}
                                    >
                                        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                                        <RadialBar background={{ fill: "#0E2B1E" }} dataKey="value" cornerRadius={20} />
                                    </RadialBarChart>
                                </ResponsiveContainer>
                                <div className="absolute inset-0 flex flex-col items-center justify-end pb-6 pointer-events-none">
                                    <div className="font-display text-5xl md:text-6xl font-bold tabular text-white">
                                        57.54<span className="text-[#D4FF00]">%</span>
                                    </div>
                                    <div className="text-xs text-white/55 mt-1">{h.semCaption}</div>
                                </div>
                            </div>

                            <div className="mt-4 p-3.5 rounded-lg bg-[#0E2B1E] border border-white/5">
                                <div className="text-[10px] uppercase tracking-widest text-[#D4FF00] font-bold mb-1">
                                    {h.verdictTitle}
                                </div>
                                <div
                                    className="text-sm text-white/85 leading-snug"
                                    dangerouslySetInnerHTML={{
                                        __html: h.verdictBody.replace(
                                            h.verdictBold,
                                            `<span class="font-bold text-white">${h.verdictBold}</span>`
                                        ),
                                    }}
                                />
                            </div>

                            <div className="absolute -top-3 -right-3 px-3 py-1.5 rounded-md bg-[#D4FF00] text-[#04140D] text-[10px] font-bold tracking-widest uppercase rotate-3 lime-glow">
                                {h.proprietary}
                            </div>
                        </div>

                        <div className="mt-4 flex items-center gap-3 text-xs text-white/45">
                            <span className="font-bold text-white/70">{h.legacy}</span>
                            <span>{h.legacyBody}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-20 md:mt-28 border-y border-white/5 py-6 overflow-hidden">
                <div className="flex gap-16 marquee-track whitespace-nowrap text-white/30 font-display uppercase tracking-[0.25em] text-sm">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} className="flex gap-16">
                            {h.marquee.map((m, j) => (
                                <span key={j}>
                                    {m}
                                    {j < h.marquee.length - 1 && (
                                        <span className="ml-16">·</span>
                                    )}
                                </span>
                            ))}
                            <span>·</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
